// 寶玉等級數據配置
export const LEVEL_DATA = {
  0: { mesoCost: 1, additionalAttribute: 100, successRate: 100, decreaseRate: 0, failRate: 0 },
  1: { mesoCost: 1, additionalAttribute: 300, successRate: 40, decreaseRate: 60, failRate: 0 },
  2: { mesoCost: 1, additionalAttribute: 300, successRate: 40, decreaseRate: 55, failRate: 5 },
  3: { mesoCost: 1, additionalAttribute: 300, successRate: 30, decreaseRate: 60, failRate: 10 },
  4: { mesoCost: 1, additionalAttribute: 300, successRate: 30, decreaseRate: 60, failRate: 10 },
  5: { mesoCost: 1, additionalAttribute: 300, successRate: 80, decreaseRate: 0, failRate: 20 },
  6: { mesoCost: 2, additionalAttribute: 400, successRate: 40, decreaseRate: 40, failRate: 20 },
  7: { mesoCost: 2, additionalAttribute: 400, successRate: 40, decreaseRate: 40, failRate: 20 },
  8: { mesoCost: 3, additionalAttribute: 400, successRate: 30, decreaseRate: 40, failRate: 30 },
  9: { mesoCost: 3, additionalAttribute: 400, successRate: 30, decreaseRate: 40, failRate: 30 },
  10: { mesoCost: 0, additionalAttribute: 400, successRate: 0, decreaseRate: 0, failRate: 0 }
}

// 升級機率配置
const UPGRADE_PROBABILITIES = {
  1: { 25: 65, 50: 5, 75: 10, 100: 20 },
  2: { 25: 65, 50: 5, 75: 10, 100: 20 },
  3: { 25: 65, 50: 5, 75: 10, 100: 20 },
  4: { 25: 70, 50: 6, 75: 9, 100: 15 },
  5: { 25: 70, 50: 6, 75: 9, 100: 15 },
  6: { 50: 75, 100: 7, 150: 8, 200: 10 },
  7: { 50: 75, 100: 7, 150: 8, 200: 10 },
  8: { 50: 80, 100: 8, 150: 7, 200: 5 },
  9: { 50: 80, 100: 8, 150: 7, 200: 5 },
  10: { 50: 85, 100: 9, 150: 4, 200: 2 }
};

// 主屬性值配置
const MAIN_ATTRIBUTE_VALUES = {
  1: [25, 50, 75, 100],
  2: [25, 50, 75, 100],
  3: [25, 50, 75, 100],
  4: [25, 50, 75, 100],
  5: [25, 50, 75, 100],
  6: [50, 100, 150, 200],
  7: [50, 100, 150, 200],
  8: [50, 100, 150, 200],
  9: [50, 100, 150, 200],
  10: [50, 100, 150, 200]
};

export class JewelCalculator {
  /**
   * 計算最終能力值
   * @param {Object} selectedMainValues - 選擇的主屬性值
   * @param {number} currentLevel - 當前等級
   * @returns {number} - 最終能力值
   */
  static calculateFinalValue(selectedMainValues, currentLevel) {
    // 計算等級1到5的主屬性（使用300%追加屬性）
    const level1to5 = Object.entries(selectedMainValues)
      .filter(([level]) => Number(level) <= 5 && Number(level) <= currentLevel)
      .reduce((sum, [_, value]) => {
        const numValue = Number(value) || 0
        return sum + numValue
      }, 0)
    
    // 計算等級6到10的主屬性（使用400%追加屬性）
    const level6to10 = Object.entries(selectedMainValues)
      .filter(([level]) => Number(level) > 5 && Number(level) <= currentLevel)
      .reduce((sum, [_, value]) => {
        const numValue = Number(value) || 0
        return sum + numValue
      }, 0)
    
    // 計算等級(當前等級+1)到10的主屬性（使用基礎追加100%）
    const inactiveLevels = Object.entries(selectedMainValues)
      .filter(([level]) => Number(level) > currentLevel)
      .reduce((sum, [_, value]) => {
        const numValue = Number(value) || 0
        return sum + numValue
      }, 0)
    
    // 計算最終能力值
    const level1to5Value = Math.floor(level1to5 * 3) // 300%
    const level6to10Value = Math.floor(level6to10 * 4) // 400%
    const inactiveValue = inactiveLevels // 基礎追加100%
    
    return level1to5Value + level6to10Value + inactiveValue
  }

  /**
   * 計算總主屬性值
   * @param {Object} selectedMainValues - 選擇的主屬性值
   * @returns {number} - 總主屬性值
   */
  static calculateTotalMainValue(selectedMainValues) {
    return Object.values(selectedMainValues).reduce((sum, value) => {
      const numValue = Number(value) || 0
      return sum + numValue
    }, 0)
  }

  /**
   * 嘗試升級並返回結果
   * @param {number} currentLevel - 當前等級
   * @returns {Object} - 升級結果，包含新等級和消耗的楓幣
   */
  static attemptUpgrade(currentLevel) {
    if (currentLevel >= 10) return { newLevel: currentLevel, mesoCost: 0 }

    const { successRate, decreaseRate, mesoCost } = LEVEL_DATA[currentLevel]
    const random = Math.random() * 100

    let newLevel = currentLevel
    if (random < successRate) {
      newLevel = currentLevel + 1
    } else if (random < successRate + decreaseRate) {
      newLevel = Math.max(0, currentLevel - 1)
    } else {
      newLevel = 0
    }

    return { newLevel, mesoCost }
  }

  /**
   * 驗證等級是否在有效範圍內
   * @param {number} level - 要驗證的等級
   * @returns {number} - 修正後的等級
   */
  static validateLevel(level) {
    if (level === '' || level === null || isNaN(level)) return 0
    return Math.max(0, Math.min(10, level))
  }

  /**
   * 獲取指定等級的隨機主屬性值
   * @param {number} level - 等級
   * @returns {number} - 隨機選擇的主屬性值
   */
  static getRandomMainValue(level) {
    const levelProbs = UPGRADE_PROBABILITIES[level];
    if (!levelProbs) return 0;

    const random = Math.random() * 100;
    let cumulativeProb = 0;

    for (const [value, prob] of Object.entries(levelProbs)) {
      cumulativeProb += prob;
      if (random < cumulativeProb) {
        return parseInt(value);
      }
    }

    // 如果沒有命中任何機率（理論上不會發生），返回最低值
    return parseInt(Object.keys(levelProbs)[0]);
  }

  /**
   * 獲取指定等級可用的主屬性值列表
   * @param {number} level - 等級
   * @returns {Array} - 可用的主屬性值列表
   */
  static getMainAttributeValues(level) {
    return MAIN_ATTRIBUTE_VALUES[level] || [];
  }

  /**
   * 檢查是否為最低的空等級
   * @param {number} level - 要檢查的等級
   * @param {Object} selectedMainValues - 當前的主屬性值
   * @returns {boolean} - 是否為最低的空等級
   */
  static isLowestEmptyLevel(level, selectedMainValues) {
    for (let i = 1; i <= level; i++) {
      if (selectedMainValues[i] === 0) {
        return i === level;
      }
    }
    return false;
  }

  /**
   * 重置所有主屬性值為0
   * @param {Object} selectedMainValues - 當前的主屬性值
   * @returns {Object} - 重置後的主屬性值
   */
  static resetMainValues(selectedMainValues) {
    const resetValues = {};
    Object.keys(selectedMainValues).forEach(key => {
      resetValues[key] = 0;
    });
    return resetValues;
  }

  /**
   * 獲取升級消耗的龍鱗數量
   * @returns {number} - 消耗的龍鱗數量
   */
  static getUpgradeDragonScaleCost() {
    return 10;
  }

  /**
   * 獲取指定等級的追加屬性百分比
   * @param {number} level - 等級
   * @returns {number} - 追加屬性百分比
   */
  static getAdditionalAttribute(level) {
    return LEVEL_DATA[level]?.additionalAttribute || 100;
  }

  /**
   * 獲取指定等級的楓幣消耗
   * @param {number} level - 等級
   * @returns {number} - 楓幣消耗
   */
  static getMesoCost(level) {
    return LEVEL_DATA[level]?.mesoCost || 0;
  }

  /**
   * 獲取指定等級的成功機率
   * @param {number} level - 等級
   * @returns {number} - 成功機率
   */
  static getSuccessRate(level) {
    return LEVEL_DATA[level]?.successRate || 0;
  }

  /**
   * 獲取指定等級的下降機率
   * @param {number} level - 等級
   * @returns {number} - 下降機率
   */
  static getDecreaseRate(level) {
    return LEVEL_DATA[level]?.decreaseRate || 0;
  }

  /**
   * 獲取指定等級的失敗機率
   * @param {number} level - 等級
   * @returns {number} - 失敗機率
   */
  static getFailRate(level) {
    return LEVEL_DATA[level]?.failRate || 0;
  }
} 